import React, { useState } from 'react';
import { WidgetMessages } from 'widgets/messages/WidgetMessages';
import { ToolbarIcon } from '@instinet-analytics/semantic-ui-components';
import ContactSupportModal from 'app/contactSupport/ContactSupportModal';
import classNames from 'classnames';
import './WidgetToolbar.scss';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import { useWidgetMessages } from 'widgets/store/stream/selectors';
import useOnclickOutside from 'react-cool-onclickoutside';
import { ToolbarIconWithText } from '@instinet-analytics/semantic-ui-components';
import LockLayoutToggle from 'layout/LockLayoutToggle/LockLayoutToggle';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import { useWidgetContext } from 'widgets/WidgetContext';
import { RemoveTileToolbarButton } from 'widgets/tiles/components/toolbar/RemoveTileToolbarButton';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { useNetwork } from 'utils/hooks/useNetwork';
import WidgetSettingsButton, {
    OnSaveHandler,
} from 'widgets/WidgetToolbar/WidgetSettingsButton';

type LayoutEditingText = { locked: string; unlocked: string };
const DefaultLayoutEditingText = {
    locked: 'Edit Legends',
    unlocked: 'Lock Legends',
};

export interface WidgetToolbarProps {
    preceedingItems?: JSX.Element;
    fixedItems?: JSX.Element;
    settingsTriggerText?: string;
    settingsHeader?: string;
    settingsBody?: JSX.Element;
    settingsClassName?: string;
    hideSettingsButton?: boolean;
    collapsibleItems?: JSX.Element[];
    hideMessages?: boolean;
    showEditLayout?: boolean;
    layoutEditingText?: LayoutEditingText;
    messages?: StreamMessage[];
    settingsSaveText?: string;
    settingsAllowCancel?: boolean;
    settingsOnSave?: OnSaveHandler;
}

export default function WidgetToolbar(props: WidgetToolbarProps) {
    const {
        preceedingItems,
        settingsTriggerText,
        settingsHeader,
        settingsBody,
        settingsSaveText,
        settingsAllowCancel,
        settingsOnSave,
        collapsibleItems,
        settingsClassName,
        fixedItems,
        hideMessages,
        showEditLayout,
        layoutEditingText: layoutEditingTextProps,
        messages,
        hideSettingsButton,
    } = props;

    const isSingleWidget = useSelectIsAppModeNewportSingleWidget();
    const hasWidgetMessages = useWidgetMessages().length > 0;
    const hasMessages =
        !hideMessages &&
        ((messages && messages.length > 0) || hasWidgetMessages);
    const isLocked = useSelectLocked();
    const { host } = useWidgetContext();

    const online = useNetwork(isSingleWidget);

    const [menuActive, setMenuState] = useState(false);
    const toggleMenu = () => setMenuState(!menuActive);
    const closeMenu = () => setMenuState(false);
    const menuRef = useOnclickOutside(closeMenu);

    const showSettings = !hideSettingsButton && settingsBody;

    let collapsibleItemsCount = collapsibleItems?.length || 0;
    if (showSettings) {
        collapsibleItemsCount++;
    }

    let showLayoutLockToggle = false;
    const layoutEditingText =
        layoutEditingTextProps || DefaultLayoutEditingText;
    const layoutEditingLabel = isLocked
        ? layoutEditingText.locked
        : layoutEditingText.unlocked;

    const isTile = host === 'tile';

    const addSingleWidgetItems = !isTile && isSingleWidget;

    if (addSingleWidgetItems) {
        collapsibleItemsCount++;

        if (showEditLayout) {
            collapsibleItemsCount++;
            showLayoutLockToggle = true;
        }
    }

    /*
     * Rendering order:
     *  - preeceding items
     *  - separator, if needed
     *  - WidgetMessages, if there are any
     *  - separator, if needed
     *  - fixed items
     *  - separator, if needed
     *  - collapsible items
     *       - includes settings modal if settings body provided
     *       - includes any provided collapsible items
     *       - includes contact support in Newport single widget mode
     *          - show settings cog if no other items, otherwise as an ellipses drop-down menu
     */
    return (
        <div
            className={classNames(
                'WidgetToolbar',
                'toolbar',
                hasMessages && 'has-messages'
            )}
        >
            {preceedingItems}
            {preceedingItems && (fixedItems || collapsibleItemsCount > 0) && (
                <div className="separator" />
            )}

            {fixedItems}
            {fixedItems && collapsibleItemsCount > 0 && (
                <div className="separator" />
            )}
            {collapsibleItemsCount > 0 && (
                <div ref={menuRef}>
                    <div
                        className={classNames(
                            'collapsible-items',
                            collapsibleItemsCount > 1 && 'collapsed',
                            menuActive && 'active'
                        )}
                        onClick={closeMenu}
                    >
                        {showSettings && (
                            <WidgetSettingsButton
                                className={settingsClassName}
                                body={settingsBody}
                                triggerText={settingsTriggerText}
                                header={settingsHeader}
                                saveText={settingsSaveText}
                                allowCancel={settingsAllowCancel}
                                onSave={settingsOnSave}
                            />
                        )}
                        {collapsibleItems || []}
                        {addSingleWidgetItems && (
                            <>
                                {showLayoutLockToggle && (
                                    <LockLayoutToggle>
                                        <ToolbarIconWithText
                                            name="edit outline"
                                            text={layoutEditingLabel}
                                        />
                                    </LockLayoutToggle>
                                )}
                                <ContactSupportModal
                                    trigger={
                                        <ToolbarIconWithText
                                            name="user circle outline"
                                            className="contact-support-button"
                                            text="Contact Support"
                                            tooltip={{
                                                body: 'Contact Support',
                                            }}
                                        />
                                    }
                                />
                            </>
                        )}
                    </div>
                    {collapsibleItemsCount > 1 && (
                        <div
                            className={classNames(
                                'ellipsis',
                                menuActive && 'active'
                            )}
                        >
                            <ToolbarIcon
                                name="ellipsis horizontal"
                                className={'view-options'}
                                onClick={toggleMenu}
                                tooltip={{ body: 'View Options' }}
                            />
                        </div>
                    )}
                </div>
            )}

            {!online && (
                <div className="network-disconnected">
                    <ToolbarIcon
                        name={'cloud'}
                        className="network-disconnected"
                        tooltip="Network disconnected"
                    />
                </div>
            )}
            {hasMessages && (
                <WidgetMessages messages={messages} hideSeparator={true} />
            )}
            {host === 'tile' && <RemoveTileToolbarButton />}
        </div>
    );
}
